import { html, nothing } from "lit";
import type { ExecutionProfileDraft } from "./types";
import { ARCHITECTURE_TYPES } from "./types";

// ─── Sequential Workflow Builder ───────────────────────────────────────────────

export type SequentialBuilderProps = {
  draft: ExecutionProfileDraft;
  onDraftUpdate: (patch: Partial<ExecutionProfileDraft>) => void;
  onAgentReorder: (from: number, to: number) => void;
  onAgentRemove: (id: string) => void;
  selectedNodeId: string | null;
  onNodeSelect: (id: string | null) => void;
};

export function renderSequentialWorkflowBuilder(props: SequentialBuilderProps) {
  const { draft, onDraftUpdate, selectedNodeId, onNodeSelect } = props;

  return html`
    <div class="swf-builder">
      <div class="swf-canvas">
        ${
          draft.agentIds.length === 0
            ? renderSequentialEmptyState()
            : html`
              <div class="swf-chain">
                <div class="swf-entry-node" @click=${() => onNodeSelect(null)}>
                  <div class="swf-node-icon">▶</div>
                  <div class="swf-node-label">Input</div>
                </div>

                <div class="swf-connector">→</div>

                ${draft.agentIds.map((agentId, i) => {
                  const override = draft.agentOverrides[agentId];
                  return html`
                    <div class="swf-agent-node ${selectedNodeId === agentId ? "swf-agent-node--selected" : ""}"
                      @click=${() => onNodeSelect(agentId)}>
                      <div class="swf-node-icon">${i === 0 ? "⬡" : "○"}</div>
                      <div class="swf-node-id mono">${agentId}</div>
                      <div class="swf-node-role muted">${override?.role ?? (i === 0 ? "lead" : "worker")}</div>
                      ${
                        draft.agentIds.length > 1
                          ? html`
                            <button
                              class="swf-node-delete"
                              @click=${(e: Event) => {
                                e.stopPropagation();
                                props.onAgentRemove(agentId);
                              }}
                              title="Remove agent"
                            >✕</button>
                          `
                          : nothing
                      }
                    </div>
                    ${
                      i < draft.agentIds.length - 1
                        ? html`
                            <div class="swf-connector">→</div>
                          `
                        : nothing
                    }
                  `;
                })}

                <div class="swf-connector">→</div>

                <div class="swf-exit-node" @click=${() => onNodeSelect(null)}>
                  <div class="swf-node-icon">■</div>
                  <div class="swf-node-label">Output</div>
                </div>
              </div>
            `
        }
      </div>

      ${selectedNodeId ? renderSequentialNodeConfig(props, selectedNodeId) : nothing}
    </div>
  `;
}

function renderSequentialEmptyState() {
  return html`
    <div class="swf-empty">
      <div class="swf-empty-icon">→</div>
      <div class="swf-empty-title">Empty Pipeline</div>
      <div class="swf-empty-desc">
        Add agents from the Agents tab to build your sequential pipeline.
      </div>
    </div>
  `;
}

function renderSequentialNodeConfig(props: SequentialBuilderProps, agentId: string) {
  const override = props.draft.agentOverrides[agentId] ?? {};
  const idx = props.draft.agentIds.indexOf(agentId);

  return html`
    <div class="swf-node-config">
      <div class="card-title">Agent: ${agentId}</div>
      <div class="swf-node-config-index muted">Position ${idx + 1} in pipeline</div>

      <label class="field" style="margin-top: 16px;">
        <span>Role</span>
        <select
          class="input"
          .value=${override.role ?? (idx === 0 ? "lead" : "worker")}
          @change=${(e: Event) => {
            const role = (e.target as HTMLSelectElement).value;
            props.onDraftUpdate({
              agentOverrides: {
                ...props.draft.agentOverrides,
                [agentId]: { ...override, role },
              },
            });
          }}
        >
          <option value="lead">Lead (first in chain)</option>
          <option value="worker">Worker</option>
          <option value="transformer">Transformer (passes data)</option>
          <option value="aggregator">Aggregator (collects results)</option>
        </select>
      </label>

      <label class="field" style="margin-top: 12px;">
        <span>Output Data Mapping</span>
        <input
          type="text"
          class="input"
          placeholder="e.g. result.summary (JSON path)"
          .value=${override.role ?? ""}
          @input=${(e: Event) => {
            // TODO: wire data mapping
          }}
        />
        <span class="field-hint">How this agent's output is passed to the next agent</span>
      </label>
    </div>
  `;
}

// ─── Supervisor Workflow Builder ──────────────────────────────────────────────

export type SupervisorBuilderProps = {
  draft: ExecutionProfileDraft;
  onDraftUpdate: (patch: Partial<ExecutionProfileDraft>) => void;
  onAgentRemove: (id: string) => void;
  selectedNodeId: string | null;
  onNodeSelect: (id: string | null) => void;
};

export function renderSupervisorWorkflowBuilder(props: SupervisorBuilderProps) {
  const { draft, selectedNodeId, onNodeSelect } = props;

  const supervisorId = draft.agentIds[0];
  const subordinates = draft.agentIds.slice(1);

  return html`
    <div class="swf-builder">
      <div class="swf-canvas">
        ${
          draft.agentIds.length < 2
            ? renderSupervisorEmptyState()
            : html`
              <div class="sup-tree">
                <div class="sup-supervisor ${selectedNodeId === supervisorId ? "swf-agent-node--selected" : ""}"
                  @click=${() => onNodeSelect(supervisorId ?? null)}>
                  <div class="swf-node-icon sup-icon">⤵</div>
                  <div class="swf-node-id mono">${supervisorId}</div>
                  <div class="swf-node-role muted">supervisor</div>
                </div>

                <div class="sup-connector-group">
                  ${subordinates.map(
                    (sub) => html`
                    <div class="sup-branch">
                      <div class="sup-branch-line"></div>
                      <div class="sup-agent ${selectedNodeId === sub ? "swf-agent-node--selected" : ""}"
                        @click=${() => onNodeSelect(sub)}>
                        <div class="swf-node-icon">○</div>
                        <div class="swf-node-id mono">${sub}</div>
                        <div class="swf-node-role muted">subordinate</div>
                        <button
                          class="swf-node-delete"
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            props.onAgentRemove(sub);
                          }}
                          title="Remove subordinate"
                        >✕</button>
                      </div>
                    </div>
                  `,
                  )}
                </div>
              </div>
            `
        }
      </div>

      ${
        selectedNodeId && selectedNodeId === supervisorId
          ? html`
            <div class="swf-node-config">
              <div class="card-title">Supervisor: ${supervisorId}</div>
              <div class="card-sub" style="margin-top: 4px;">
                The supervisor delegates tasks to subordinates and aggregates results.
              </div>
            </div>
          `
          : selectedNodeId
            ? html`
              <div class="swf-node-config">
                <div class="card-title">Subordinate: ${selectedNodeId}</div>
                <div class="card-sub" style="margin-top: 4px;">
                  Configure delegation conditions and result handling.
                </div>
              </div>
            `
            : nothing
      }
    </div>
  `;
}

function renderSupervisorEmptyState() {
  return html`
    <div class="swf-empty">
      <div class="swf-empty-icon">⤵</div>
      <div class="swf-empty-title">Supervisor Architecture</div>
      <div class="swf-empty-desc">
        Add at least 2 agents — one as supervisor and one or more subordinates. The first agent added
        becomes the supervisor.
      </div>
    </div>
  `;
}

// ─── Parallel Workflow Builder ─────────────────────────────────────────────────

export type ParallelBuilderProps = {
  draft: ExecutionProfileDraft;
  onDraftUpdate: (patch: Partial<ExecutionProfileDraft>) => void;
  onAgentRemove: (id: string) => void;
  onAgentAdd: (id: string) => void;
  selectedNodeId: string | null;
  onNodeSelect: (id: string | null) => void;
};

export function renderParallelWorkflowBuilder(props: ParallelBuilderProps) {
  const { draft, selectedNodeId, onNodeSelect } = props;

  const primaryId = draft.agentIds[0];
  const parallelAgents = draft.agentIds.slice(1);

  return html`
    <div class="swf-builder">
      <div class="swf-canvas">
        ${
          draft.agentIds.length < 2
            ? renderParallelEmptyState()
            : html`
              <div class="para-layout">
                <div class="para-entry">
                  <div class="para-entry-node ${selectedNodeId === primaryId ? "swf-agent-node--selected" : ""}"
                    @click=${() => onNodeSelect(primaryId ?? null)}>
                    <div class="swf-node-icon">▶</div>
                    <div class="swf-node-id mono">${primaryId}</div>
                    <div class="swf-node-role muted">fan-out</div>
                  </div>
                </div>

                <div class="para-bus">
                  <div class="para-bus-line"></div>
                  ${parallelAgents.map(
                    (id) => html`
                      <div class="para-connector-v">|</div>
                    `,
                  )}
                </div>

                <div class="para-agents">
                  ${parallelAgents.map((agentId) => {
                    const override = draft.agentOverrides[agentId] ?? {};
                    return html`
                      <div class="para-agent-card ${selectedNodeId === agentId ? "swf-agent-node--selected" : ""}"
                        @click=${() => onNodeSelect(agentId)}>
                        <div class="swf-node-icon">○</div>
                        <div class="swf-node-id mono">${agentId}</div>
                        <div class="swf-node-role muted">${override.role ?? "parallel"}</div>
                        <div class="para-merge-label muted">↓ merge</div>
                        <button
                          class="swf-node-delete"
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            props.onAgentRemove(agentId);
                          }}
                          title="Remove"
                        >✕</button>
                      </div>
                    `;
                  })}
                </div>

                <div class="para-merge">
                  <div class="para-merge-node">
                    <div class="swf-node-icon">⬡</div>
                    <div class="swf-node-label">Merge</div>
                  </div>
                </div>
              </div>
            `
        }
      </div>

      ${
        selectedNodeId && selectedNodeId === primaryId
          ? html`<div class="swf-node-config"><div class="card-title">Fan-out: ${primaryId}</div></div>`
          : selectedNodeId
            ? html`<div class="swf-node-config"><div class="card-title">Parallel Agent: ${selectedNodeId}</div></div>`
            : nothing
      }
    </div>
  `;
}

function renderParallelEmptyState() {
  return html`
    <div class="swf-empty">
      <div class="swf-empty-icon">⤴</div>
      <div class="swf-empty-title">Parallel Architecture</div>
      <div class="swf-empty-desc">
        Add at least 2 agents. The first is the fan-out agent; all others run in parallel. Results are
        merged at the end.
      </div>
    </div>
  `;
}

// ─── Custom Graph Builder (placeholder for JAISIU-854) ────────────────────────

export type CustomGraphBuilderProps = {
  draft: ExecutionProfileDraft;
  onDraftUpdate: (patch: Partial<ExecutionProfileDraft>) => void;
  onAgentAdd: (id: string) => void;
  onAgentRemove: (id: string) => void;
};

export function renderCustomGraphBuilder(_props: CustomGraphBuilderProps) {
  return html`
    <div class="swf-builder">
      <div
        class="swf-canvas"
        style="
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding-top: 32px;
        "
      >
        <div class="swf-empty">
          <div class="swf-empty-icon">⬡</div>
          <div class="swf-empty-title">Custom Graph Editor</div>
          <div class="swf-empty-desc">
            Free-form graph editing with arbitrary node connections, cycle detection, and conditional
            routing. Add agents from the Agents tab, then connect them here.
          </div>
        </div>

        <div class="callout info" style="margin: 24px 32px; max-width: 600px">
          <strong>Full implementation:</strong> JAISIU-854 — Custom Graph Editor. This is the UI
          scaffold. The drag-and-drop canvas with edge configuration and cycle detection is implemented
          as part of that ticket.
        </div>
      </div>
    </div>
  `;
}

// ─── Graph Validator (JAISIU-855) ──────────────────────────────────────────────

export type ValidationResult = {
  valid: boolean;
  errors: { nodeId?: string; message: string; suggestion?: string }[];
  warnings: { nodeId?: string; message: string }[];
};

export function validateExecutionGraph(draft: ExecutionProfileDraft): ValidationResult {
  const errors: ValidationResult["errors"] = [];
  const warnings: ValidationResult["warnings"] = [];

  if (draft.agentIds.length === 0) {
    errors.push({ message: "No agents in profile." });
    return { valid: false, errors, warnings };
  }

  // Sequential validation
  if (draft.architectureType === "sequential") {
    if (draft.agentIds.length < 2) {
      errors.push({ message: "Sequential pipeline requires at least 2 agents." });
    }
  }

  // Supervisor validation
  if (draft.architectureType === "supervisor") {
    if (draft.agentIds.length < 2) {
      errors.push({
        message: "Supervisor architecture requires at least 2 agents (supervisor + subordinate).",
        suggestion: "Add at least one subordinate agent.",
      });
    }
    if (draft.agentIds.length > 8) {
      warnings.push({
        message:
          "Supervisor with more than 8 subordinates may be hard to manage. Consider grouping.",
      });
    }
  }

  // Parallel validation
  if (draft.architectureType === "parallel") {
    if (draft.agentIds.length < 2) {
      errors.push({ message: "Parallel architecture requires at least 2 parallel agents." });
    }
    if (draft.agentIds.length > 16) {
      warnings.push({
        message: "More than 16 parallel agents may hit rate limits or cost budgets.",
      });
    }
  }

  // Custom graph: detect orphan nodes
  if (draft.architectureType === "custom") {
    // All agents are orphans in custom mode without explicit edges defined
    if (draft.agentIds.length > 10) {
      warnings.push({
        message: "Custom graph has many nodes. Ensure connectivity is validated before running.",
      });
    }
  }

  // Generic checks
  if (!draft.defaultModelId) {
    warnings.push({ message: "No default model set. May fall back to global default." });
  }

  return { valid: errors.length === 0, errors, warnings };
}

export function renderValidationResult(result: ValidationResult) {
  if (result.valid && result.warnings.length === 0) {
    return html`
      <div class="callout ok">✓ Profile is valid. Ready to save and run.</div>
    `;
  }

  return html`
    ${
      result.errors.length > 0
        ? html`
          <div class="callout danger" style="margin-bottom: 8px;">
            <strong>${result.errors.length} error(s):</strong>
            <ul>
              ${result.errors.map(
                (e) => html`
                  <li>
                    ${e.nodeId ? html`<span class="mono">[${e.nodeId}]</span> ` : ""}
                    ${e.message}
                    ${e.suggestion ? html`<br/><em>→ ${e.suggestion}</em>` : nothing}
                  </li>
                `,
              )}
            </ul>
          </div>
        `
        : nothing
    }
    ${
      result.warnings.length > 0
        ? html`
          <div class="callout warn">
            <strong>${result.warnings.length} warning(s):</strong>
            <ul>
              ${result.warnings.map(
                (w) => html`
                  <li>
                    ${w.nodeId ? html`<span class="mono">[${w.nodeId}]</span> ` : ""}
                    ${w.message}
                  </li>
                `,
              )}
            </ul>
          </div>
        `
        : nothing
    }
  `;
}
